import React, { useRef, useState, useEffect } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
// Helper function to draw an image with "cover" effect, preserving aspect ratio
const drawImageCover = (ctx, img, x, y, w, h) => {
  const imgRatio = img.width / img.height;
  const targetRatio = w / h;
  let sWidth = img.width;
  let sHeight = img.height;
  let sx = 0;
  let sy = 0;

  if (imgRatio > targetRatio) {
    // Image is wider than target, crop horizontally
    sWidth = img.height * targetRatio;
    sx = (img.width - sWidth) / 2;
  } else {
    // Image is taller than target, crop vertically
    sHeight = img.width / targetRatio;
    sy = (img.height - sHeight) / 2;
  }

  ctx.drawImage(img, sx, sy, sWidth, sHeight, x, y, w, h);
};

function StreamingImageMerger() {
  const [leftImg, setLeftImg] = useState(null);
  const [rightImg, setRightImg] = useState(null);
  const [centerImg, setCenterImg] = useState(null);
  const [resultUrl, setResultUrl] = useState(null);
  const [bottomText, setBottomText] = useState("");
  const [textStyle, setTextStyle] = useState("white"); // "white" or "black"
  const [middleText, setMiddleText] = useState("");
  const [tempMiddleText, setTempMiddleText] = useState(""); // 입력용 임시 상태
  const [logoOpacity, setLogoOpacity] = useState(1);
  const [tempLogoOpacity, setTempLogoOpacity] = useState(1); // 임시 상태
  const [subTitleText, setSubTitleText] = useState(""); // 제목 아래 작은 텍스트
  const canvasRef = useRef(null);

  const handleImageChange = async (e, setter) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new window.Image();
        img.onload = () => {
          setter(img);
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    }
  };
  const timeSetForImageName = () => {
    return "streaming_" + dayjs().tz("Asia/Seoul").format("YYYYMMDD_HHmmss");
  };

  const addCurrentTime = () => {
    const timeString = dayjs().tz("Asia/Seoul").format("YYYY-MM-DD HH:mm");
    setBottomText(timeString);
    if (leftImg && rightImg && centerImg) {
      mergeImages(null, textStyle, timeString);
    }
  };

  const mergeImages = async (
    newImages = null,
    style = textStyle,
    customText = null
  ) => {
    const images = newImages || {
      left: leftImg,
      right: rightImg,
      center: centerImg,
    };
    if (!images.left || !images.right || !images.center) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // 이미지가 Image 객체가 아니면 변환
    const getImageObject = (img) => {
      if (img instanceof window.Image) return img;
      if (typeof img === "string") {
        const imageObj = new window.Image();
        imageObj.src = img;
        return imageObj;
      }
      return null;
    };
    const leftImage = getImageObject(images.left);
    const rightImage = getImageObject(images.right);
    const centerImage = getImageObject(images.center);
    if (!leftImage || !rightImage || !centerImage) return;

    // 이미지가 아직 로드 안 됐으면 onload 후 다시 실행
    if (
      !leftImage.complete ||
      !rightImage.complete ||
      !centerImage.complete ||
      leftImage.naturalWidth === 0 ||
      rightImage.naturalWidth === 0 ||
      centerImage.naturalWidth === 0
    ) {
      leftImage.onload =
        rightImage.onload =
        centerImage.onload =
          () => {
            mergeImages(images, style, customText);
          };
      return;
    }

    // Calculate dynamic canvas size
    const leftHeight = leftImage.naturalHeight || leftImage.height;
    const rightHeight = rightImage.naturalHeight || rightImage.height;
    const canvasHeight = Math.max(leftHeight, rightHeight);

    const leftNewWidth = leftImage.naturalWidth
      ? leftImage.naturalWidth * (canvasHeight / leftHeight)
      : leftImage.width * (canvasHeight / leftHeight);
    const rightNewWidth = rightImage.naturalWidth
      ? rightImage.naturalWidth * (canvasHeight / rightHeight)
      : rightImage.width * (canvasHeight / rightHeight);
    const canvasWidth = leftNewWidth + rightNewWidth;

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Draw images without cropping
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.drawImage(leftImage, 0, 0, leftNewWidth, canvasHeight);
    ctx.drawImage(rightImage, leftNewWidth, 0, rightNewWidth, canvasHeight);

    // Draw center logo (it will be cropped to a circle)
    const centerSize = canvasHeight * 0.5; // 50% of canvas height
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    ctx.save();
    ctx.globalAlpha = logoOpacity;
    ctx.beginPath();
    ctx.arc(centerX, centerY, centerSize / 2, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.clip();
    drawImageCover(
      ctx,
      centerImage,
      centerX - centerSize / 2,
      centerY - centerSize / 2,
      centerSize,
      centerSize
    );
    ctx.globalAlpha = 1;
    ctx.restore();

    // --- Dynamic Text Drawing ---
    const baseFontSize = canvasHeight * 0.045; // ~4.5% of height
    const lineWidth = baseFontSize / 4; // ~25% of font size for the stroke

    // middleText
    if (middleText.trim() !== "") {
      ctx.save();
      const fontSize = baseFontSize;
      ctx.font = `bold ${fontSize}px Pretendard, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      const y = canvasHeight - canvasHeight * 0.13; // Position at ~87% from top
      if (textStyle === "white") {
        ctx.strokeStyle = "#000";
        ctx.lineWidth = lineWidth;
        ctx.strokeText(middleText, canvasWidth / 2, y);
        ctx.fillStyle = "#fff";
        ctx.fillText(middleText, canvasWidth / 2, y);
      } else {
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = lineWidth;
        ctx.strokeText(middleText, canvasWidth / 2, y);
        ctx.fillStyle = "#000";
        ctx.fillText(middleText, canvasWidth / 2, y);
      }
      ctx.restore();
    }

    // bottomText (time)
    const drawText = customText !== null ? customText : bottomText;
    if (drawText.trim() !== "") {
      ctx.save();
      const fontSize = baseFontSize;
      ctx.font = `bold ${fontSize}px Pretendard, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      const y = canvasHeight - canvasHeight * 0.05; // Position at ~95% from top
      if (style === "white") {
        ctx.strokeStyle = "#000";
        ctx.lineWidth = lineWidth;
        ctx.strokeText(drawText, canvasWidth / 2, y);
        ctx.fillStyle = "#fff";
        ctx.fillText(drawText, canvasWidth / 2, y);
      } else {
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = lineWidth;
        ctx.strokeText(drawText, canvasWidth / 2, y);
        ctx.fillStyle = "#000";
        ctx.fillText(drawText, canvasWidth / 2, y);
      }
      ctx.restore();
    }

    setResultUrl(canvas.toDataURL("image/png"));
  };

  // 컴포넌트 마운트 시 로고 기본값 세팅
  useEffect(() => {
    const setDefaultLogo = async () => {
      const response = await fetch("/qwer.webp");
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new window.Image();
        img.onload = () => {
          setCenterImg(img);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(blob);
    };
    setDefaultLogo();
  }, []);

  useEffect(() => {
    if (leftImg && rightImg && centerImg) {
      mergeImages();
    }
  }, [
    textStyle,
    leftImg,
    rightImg,
    centerImg,
    middleText,
    bottomText,
    logoOpacity,
  ]);

  return (
    <div
      style={{
        textAlign: "center",
        maxWidth: 420,
        margin: "40px auto",
        background: "#bcc1b8",
        borderRadius: 24,
        boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
        padding: 32,
        fontFamily: "Pretendard, sans-serif",
      }}
    >
      <h2
        style={{
          marginBottom: 24,
          fontWeight: 700,
          fontSize: 24,
          color: "#222",
        }}
      >
        스트리밍 이미지 메이커
      </h2>
      <div
        style={{
          marginBottom: 10,
          fontSize: 12,
          color: "#000",
          textAlign: "center",
        }}
      >
        사진 업로드 이후 텍스트 입력,투명도 조절 랙걸리는 현상 개선
      </div>

      <div
        style={{
          marginBottom: 24,
          background: "#f7f7fa",
          borderRadius: 16,
          padding: 20,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontWeight: 500, color: "#444" }}>
            좌측 이미지
            <br />
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(e, setLeftImg)}
              style={{ marginTop: 4 }}
            />
          </label>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontWeight: 500, color: "#444" }}>
            우측 이미지
            <br />
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(e, setRightImg)}
              style={{ marginTop: 4 }}
            />
          </label>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 500, color: "#444" }}>
            로고 이미지(기본로고 적용)
            <br />
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(e, setCenterImg)}
              style={{ marginTop: 4 }}
            />
          </label>
        </div>
        <div style={{ marginBottom: 12 }}>
          <input
            type="text"
            value={tempMiddleText}
            onChange={(e) => {
              setTempMiddleText(e.target.value);
            }}
            placeholder="원하는 문구를 입력하세요"
            style={{
              width: "100%",
              fontSize: 16,
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #bbb",
              boxSizing: "border-box",
            }}
          />
          <button
            style={{
              marginLeft: 8,
              padding: "7px 18px",
              borderRadius: 8,
              border: "none",
              background: "#7B8CDE",
              color: "#fff",
              fontWeight: 600,
              fontSize: 15,
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(108,71,255,0.08)",
              transition: "all 0.18s",
            }}
            onClick={() => {
              setMiddleText(tempMiddleText);
            }}
          >
            텍스트 반영
          </button>
        </div>
        <div
          style={{
            marginBottom: 18,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <span style={{ fontSize: 15, color: "#444", minWidth: 70 }}>
            로고 투명도
          </span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(tempLogoOpacity * 100)}
            onChange={(e) => setTempLogoOpacity(Number(e.target.value) / 100)}
            onMouseUp={() => setLogoOpacity(tempLogoOpacity)}
            onTouchEnd={() => setLogoOpacity(tempLogoOpacity)}
            style={{ flex: 1 }}
          />
          <span style={{ fontSize: 15, color: "#444", minWidth: 36 }}>
            {Math.round(tempLogoOpacity * 100)}%
          </span>
        </div>
        <div style={{ fontSize: 13, color: "#888", marginTop: 10 }}>
          ※ 이미지 2장을 모두 업로드하면 자동으로 미리보기가 생성됩니다, 로고
          이미지 변경가능
        </div>
      </div>
      <div style={{ margin: "24px 0" }}>
        <canvas
          ref={canvasRef}
          style={{
            display: resultUrl ? "none" : "block",
            border: "1px solid #eee",
            borderRadius: 16,
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
            width: "100%",
            maxWidth: 350,
            margin: "0 auto",
          }}
          width={1400}
          height={1400}
        />
        {resultUrl && (
          <div
            style={{
              marginTop: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <img
              src={resultUrl}
              alt="합성 결과"
              style={{
                maxWidth: "100%",
                borderRadius: 20,
                boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
                border: "1.5px solid #eaeaea",
              }}
            />
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          gap: 10,
          margin: "18px 0 0 0",
          width: "100%",
          flexWrap: "nowrap",
          padding: "0 16px",
        }}
      >
        <button
          onClick={addCurrentTime}
          style={{
            flex: 1,
            minWidth: 170,
            maxWidth: "none",
            height: 40,
            background: "#E98DB6",
            border: "none",
            borderRadius: 20,
            fontSize: 18,
            color: "#fff",
            cursor: "pointer",
            fontWeight: 700,
            letterSpacing: "-1px",
            boxShadow: "0 2px 12px rgba(108,71,255,0.13)",
            transition: "all 0.18s",
            outline: "none",
            lineHeight: "40px",
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = "#d86ca2")}
          onMouseOut={(e) => (e.currentTarget.style.background = "#E98DB6")}
        >
          시간 입력
        </button>
        <button
          onClick={() => {
            const nextStyle = textStyle === "white" ? "black" : "white";
            setTextStyle(nextStyle);
          }}
          style={{
            flex: 1,
            minWidth: 170,
            maxWidth: "none",
            height: 40,
            background: "#3DD6F0",
            border: "none",
            borderRadius: 20,
            fontSize: 18,
            color: "#fff",
            cursor: "pointer",
            fontWeight: 700,
            letterSpacing: "-1px",
            boxShadow: "0 2px 12px rgba(108,71,255,0.13)",
            transition: "all 0.18s",
            outline: "none",
            lineHeight: "40px",
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = "#1bbad6")}
          onMouseOut={(e) => (e.currentTarget.style.background = "#3DD6F0")}
        >
          텍스트 스타일 변경
        </button>
      </div>
      <div style={{ fontSize: 12, color: "#000000", marginTop: 18 }}>
        iOS는 이미지 꾹 눌러서 다운로드 하세요
      </div>
      {resultUrl && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            margin: "18px 0 0 0",
          }}
        >
          <a
            href={resultUrl}
            download={timeSetForImageName() + ".png"}
            style={{
              textDecoration: "none",
              display: "inline-block",
            }}
          >
            <button
              style={{
                background: "#C7D86B",
                color: "#222",
                border: "none",
                borderRadius: 8,
                padding: "10px 28px",
                fontWeight: 600,
                fontSize: 15,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                transition: "all 0.18s",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background = "#b0c95a")
              }
              onMouseOut={(e) => (e.currentTarget.style.background = "#C7D86B")}
            >
              다운로드
            </button>
          </a>
        </div>
      )}
    </div>
  );
}

export default StreamingImageMerger;
